import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { casesAPI, reportsAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis,
} from 'recharts';
import {
  FolderOpen, Calendar, Send, AlertTriangle, Clock,
  MapPin, Gavel, ChevronRight, Users, TrendingUp,
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  Active: '#1e40af',
  Pending: '#0ea5e9',
  Adjourned: '#f59e0b',
  Disposed: '#10b981',
};

/* ── Personal greeting ─────────────────────────────────────────── */
function greetingFor(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/* ── Stat Card (clickable) ─────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, sublabel, linkTo, accent = 'blue' }) {
  const accentMap = {
    blue:   { bar: 'bg-blue-600',   text: 'text-blue-600',   bg: 'bg-blue-50' },
    orange: { bar: 'bg-orange-500', text: 'text-orange-500', bg: 'bg-orange-50' },
    green:  { bar: 'bg-green-600',  text: 'text-green-600',  bg: 'bg-green-50' },
    red:    { bar: 'bg-red-500',    text: 'text-red-500',    bg: 'bg-red-50' },
    purple: { bar: 'bg-purple-600', text: 'text-purple-600', bg: 'bg-purple-50' },
  };
  const a = accentMap[accent] || accentMap.blue;

  const inner = (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${a.bg} ${a.text}`}>
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold text-gray-900 leading-tight">{value ?? '—'}</p>
          <p className="text-sm text-gray-500 mt-0.5">{label}</p>
        </div>
        {linkTo && <ChevronRight size={16} className="text-gray-300 shrink-0" />}
      </div>
      {sublabel && (
        <p className={`text-xs ${a.text} font-medium mt-3`}>{sublabel}</p>
      )}
    </div>
  );

  return linkTo ? <Link to={linkTo}>{inner}</Link> : inner;
}

/* ── Upcoming hearing row (next 7 days) ──────────────────────── */
function UpcomingHearingRow({ h }) {
  const dateObj = h.next_hearing_date ? new Date(h.next_hearing_date) : null;
  const daysLeft = dateObj ? differenceInDays(dateObj, new Date()) : null;
  const urgent = daysLeft !== null && daysLeft <= 1;

  return (
    <Link
      to={`/cases/${h.id}`}
      className={`flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors ${
        urgent ? 'bg-red-50' : ''
      }`}
    >
      <div className="text-center min-w-[44px]">
        {dateObj ? (
          <>
            <p className="text-[10px] font-bold text-gray-400 uppercase leading-none">
              {format(dateObj, 'MMM')}
            </p>
            <p className="text-lg font-bold text-gray-900 leading-tight">
              {format(dateObj, 'dd')}
            </p>
            <p className="text-[10px] text-gray-400">{format(dateObj, 'EEE')}</p>
          </>
        ) : (
          <p className="text-xs text-gray-400">—</p>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{h.title || h.case_number}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
          <MapPin size={10} className="shrink-0" />
          <span className="truncate">{h.court_name}</span>
        </p>
      </div>
      {daysLeft !== null && (
        <div
          className={`flex items-center gap-1 text-xs font-semibold shrink-0 ${
            urgent ? 'text-red-500' : 'text-gray-400'
          }`}
        >
          {urgent ? <AlertTriangle size={12} /> : <Clock size={12} />}
          {daysLeft === 0 ? 'Today' : daysLeft === 1 ? 'Tomorrow' : `${daysLeft}d`}
        </div>
      )}
    </Link>
  );
}

/* ── Dashboard Page ───────────────────────────────────────────── */
export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      casesAPI.dashboardStats().then(r => setStats(r.data)).catch(() => {}),
      reportsAPI.dashboard().then(r => setReport(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleSendReport = async () => {
    setSending(true);
    try {
      const res = await reportsAPI.sendDaily();
      toast.success(res.data?.message || 'Daily report sent!');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send report';
      toast.error(msg, { duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  const firstName = user?.first_name || user?.full_name?.split(' ')[0] || 'there';
  const isAdmin = user?.role === 'admin';

  // Pie data — only show non-zero buckets so empty state is clean
  const pieData = stats ? [
    { name: 'Active',    value: Math.max((stats.active_cases || 0) - (stats.adjourned_cases || 0) - (stats.pending_cases || 0), 0) },
    { name: 'Pending',   value: stats.pending_cases   || 0 },
    { name: 'Adjourned', value: stats.adjourned_cases || 0 },
    { name: 'Disposed',  value: stats.disposed_cases  || 0 },
  ].filter(d => d.value > 0) : [];

  const lawyerBarData = report?.lawyer_stats?.map(l => ({
    name: l.name,
    Active:    l.active,
    Adjourned: l.adjourned,
    Disposed:  l.disposed,
  })) || [];

  // Upcoming hearings (next 7 days) come from the reports endpoint
  const upcomingHearings = (report?.upcoming_hearings || []).slice(0, 8);

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {greetingFor()}, {firstName} 👋
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {format(new Date(), 'EEEE, dd MMMM yyyy')}
            {stats && stats.hearings_today > 0 && (
              <span className="ml-2 text-blue-700 font-medium">
                · You have {stats.hearings_today} hearing{stats.hearings_today > 1 ? 's' : ''} today
              </span>
            )}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={handleSendReport}
            disabled={sending}
            className="flex items-center gap-2 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors disabled:opacity-60 hover:opacity-90"
            style={{ backgroundColor: '#1a2744' }}
          >
            <Send size={15} />
            {sending ? 'Sending…' : 'Send Daily Report'}
          </button>
        )}
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={FolderOpen}
          label={isAdmin ? 'Total Cases' : 'My Cases'}
          value={stats?.total_cases}
          sublabel={stats?.active_cases > 0 ? `${stats.active_cases} active` : null}
          linkTo="/cases"
          accent="blue"
        />
        <StatCard
          icon={Gavel}
          label="Hearings Today"
          value={stats?.hearings_today}
          sublabel={stats?.hearings_today > 0 ? 'Action needed' : "You're clear"}
          accent={stats?.hearings_today > 0 ? 'orange' : 'green'}
        />
        <StatCard
          icon={Calendar}
          label="This Week"
          value={stats?.hearings_this_week}
          sublabel="Hearings in next 7 days"
          accent="purple"
        />
      </div>

      {/* ── Status pie + Upcoming hearings side-by-side ────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Status pie — 1/3 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
            <div className="p-1.5 bg-purple-100 text-purple-700 rounded-lg">
              <TrendingUp size={14} />
            </div>
            <h2 className="font-semibold text-gray-800">Status Distribution</h2>
          </div>
          <div className="p-4">
            {loading ? (
              <p className="text-center text-gray-400 text-sm py-16">Loading…</p>
            ) : pieData.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-16">No cases yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%" cy="45%"
                    outerRadius={85} innerRadius={50}
                    dataKey="value"
                    labelLine={false}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                      if (!value) return null;
                      const RADIAN = Math.PI / 180;
                      const r = innerRadius + (outerRadius - innerRadius) * 0.5;
                      const x = cx + r * Math.cos(-midAngle * RADIAN);
                      const y = cy + r * Math.sin(-midAngle * RADIAN);
                      return (
                        <text x={x} y={y} fill="white" textAnchor="middle"
                          dominantBaseline="central" fontSize={12} fontWeight="bold">
                          {value}
                        </text>
                      );
                    }}
                  >
                    {pieData.map((d, i) => (
                      <Cell key={i} fill={STATUS_COLORS[d.name] || '#94a3b8'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v} cases`, n]} />
                  <Legend
                    iconType="circle"
                    iconSize={9}
                    formatter={(value, entry) => (
                      <span style={{ color: '#374151', fontSize: 12 }}>
                        {value}{' '}
                        <span style={{ color: entry.color, fontWeight: 600 }}>
                          ({entry.payload.value})
                        </span>
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Upcoming hearings — 2/3 */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-orange-100 text-orange-700 rounded-lg">
                <Calendar size={14} />
              </div>
              <h2 className="font-semibold text-gray-800">Upcoming Hearings</h2>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                Next 7 days
              </span>
            </div>
            <Link to="/cases" className="text-xs font-medium text-blue-600 hover:text-blue-800">
              View all →
            </Link>
          </div>
          <div className="divide-y divide-gray-50 p-2 max-h-[420px] overflow-y-auto">
            {loading ? (
              <p className="text-center text-gray-400 text-sm py-10">Loading…</p>
            ) : upcomingHearings.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-10">No upcoming hearings in the next 7 days</p>
            ) : (
              upcomingHearings.map((h, i) => <UpcomingHearingRow key={i} h={h} />)
            )}
          </div>
        </div>
      </div>

      {/* ── Lawyer-wise breakdown (admin only) ─────────────────── */}
      {isAdmin && lawyerBarData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
            <div className="p-1.5 bg-blue-100 text-blue-700 rounded-lg">
              <Users size={14} />
            </div>
            <h2 className="font-semibold text-gray-800">Lawyer-wise Cases</h2>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={lawyerBarData} barSize={16}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: '#f8fafc' }} />
                <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Active"    fill="#1e40af" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Adjourned" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Disposed"  fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
